import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ExpandMore, ExpandLess, ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { useSelector } from "react-redux";
import PropTypes from 'prop-types';
import apiService from '../../../actions/services';

const getFirstDayOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
};

const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

const tiendasPrecargadas = [
  { id: 1, nombre: 'TZ10' },
  { id: 2, nombre: 'TPRR' },
  { id: 3, nombre: 'TSCR' },
  { id: 4, nombre: 'TCSV' },
  { id: 5, nombre: 'TMIR' },
  { id: 6, nombre: 'TXLA' }
];

const ListTransferencia = () => {
  const { user } = useSelector(state => state.auth);  
  const [availableItemsTSCR, setAvailableItemsTSCR] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});
  const [selectedSala, setSelectedSala] = useState("TZ10");
  const [filteredTiendas, setFilteredTiendas] = useState([]);
  const [fechaIni, setFechaIni] = useState(getFirstDayOfMonth());
  const [fechaFin, setFechaFin] = useState(getCurrentDate());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getTransferType = (transferencia) => {
    const sala = selectedSala;
    const origen = transferencia.AlmacénOrigen || '';
    const destino = transferencia.AlmacénDestino || '';
    
    if (origen.includes(sala) && destino.includes(sala)) {
      return { 
        type: 'interno', 
        color: 'rgba(173, 216, 230, 0.3)', // Azul claro
        icon: <Typography component="span" sx={{ 
          fontWeight: 'bold', 
          fontSize: '1.2rem',
          color: 'primary.main'
        }}>≡</Typography>
      };
    } else if (origen.includes(sala)) {
      return { 
        type: 'salida', 
        color: 'rgba(255, 0, 0, 0.1)',
        icon: <ArrowUpward color="error" />
      };
    } else if (destino.includes(sala) || destino.includes(`${sala}-PLT`)) {
      return { 
        type: 'entrada', 
        color: 'rgba(0, 255, 0, 0.1)',
        icon: <ArrowDownward color="success" />
      };
    }
    return { 
      type: 'otro', 
      color: 'transparent',
      icon: null
    };
  };

  useEffect(() => {
    if (user && user.usuario) {
      const dat = {};
      apiService.logactivity('listaTraslado.jsx','',dat,user);
      const tiendaUsuario = tiendasPrecargadas.find(tienda => 
        tienda.nombre.toLowerCase() === user.usuario.toLowerCase() ||
        user.usuario.toLowerCase().includes(tienda.nombre.toLowerCase())
      );

      if (tiendaUsuario) {
        setFilteredTiendas([tiendaUsuario]);
        setSelectedSala(tiendaUsuario.nombre);
      } else {
        setFilteredTiendas(tiendasPrecargadas);
      }
    } else {
      setFilteredTiendas(tiendasPrecargadas);
    }
  }, [user]);

  const fetchData = useCallback((sala, fechaInicio, fechaFinal) => {
    setIsLoading(true);
    setError(null);
    
    const datos = {
      Token: process.env.REACT_APP_API_TOKEN || "LJKNFUH2FWEJBAEFFFFFKJ VGDEA003CSFCAQWEUIRRRRRRRRRRRADFYGBIFHVBESRNYHIOPWJROTY4WY",
      Ambiente: "DESA",
      Dispositivo: {
        Marca: "SAMSUNG",
        Modelo: "S22002B",
        Version: "Android 18.125",
        McAdress: null,
        PowerStatus: "84%",
        WifiStatus: "Conected to iphone de Mauricio",
        GPSStatus: "ON",
        GPS: "321|-485|321",
      },
      Usr: {
        Codigo: "",
        UserNickName: "pierril",
      },
      DatosMetodo: {
        NombreMetodo: "ListaTransferenciasSalas",
        Datos_Recibidos: JSON.stringify({
          FechaIni: fechaInicio,
          FechaFin: fechaFinal,
          Sala: sala
        }),
      },
    };

    fetch(`https://appcorp.parma.com.gt/API/ReceptorDatos?Datos=${encodeURIComponent(JSON.stringify(datos))}`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (data.datos_Generados) {
          const decodedString = JSON.parse(data.datos_Generados);
          setAvailableItemsTSCR(decodedString.TRANSFERENCIAS || []);
        } else {
          setError("Datos recibidos no válidos");
        }
      })
      .catch(error => {
        console.error("Error:", error);
        setError(error.message);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (selectedSala) fetchData(selectedSala, fechaIni, fechaFin);
  }, [selectedSala, fetchData, fechaIni, fechaFin]);

  const handleExpandClick = (id) => {
    setOpen(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSalaChange = (event) => {
    setSelectedSala(event.target.value);
  };

  const handleSearch = () => {
    if (selectedSala) fetchData(selectedSala, fechaIni, fechaFin);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Lista de Transferencias
      </Typography>
      
      <Box display="flex" gap={2} alignItems="center" mb={4} flexDirection={isMobile ? "column" : "row"}>
        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel id="sala-select-label">Sala</InputLabel>
          <Select
            labelId="sala-select-label"
            value={selectedSala}
            label="Sala"
            onChange={handleSalaChange}
          >
            {filteredTiendas.map(tienda => (
              <MenuItem key={tienda.id} value={tienda.nombre}>{tienda.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Fecha Inicio"
          type="date"
          value={fechaIni}
          onChange={e => setFechaIni(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size={isMobile ? "small" : "medium"}
        />

        <TextField
          label="Fecha Fin"
          type="date"
          value={fechaFin}
          onChange={e => setFechaFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          size={isMobile ? "small" : "medium"}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={isLoading}
          style={{ height: isMobile ? '40px' : '56px', minWidth: '120px' }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Buscar'}
        </Button>
      </Box>

      {error && <Typography color="error" gutterBottom>Error: {error}</Typography>}
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : availableItemsTSCR.length === 0 ? (
        <Typography variant="body1" align="center" my={4}>
          No se encontraron transferencias
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Transferencia ID</TableCell>
                {!isMobile && <TableCell>Número Transferencia</TableCell>}
                <TableCell>Fecha Documento</TableCell>
                <TableCell>Almacén Origen</TableCell>
                <TableCell>Almacén Destino</TableCell>
                {!isMobile && <TableCell>Comentarios</TableCell>}
                <TableCell>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableItemsTSCR.map(transferencia => {
                const transferType = getTransferType(transferencia);
                return (
                  <React.Fragment key={transferencia.TransferenciaID}>
                    <TableRow sx={{ backgroundColor: transferType.color }}>
                      <TableCell>
                        {transferType.icon}
                      </TableCell>

                  {/* <React.Fragment key={transferencia.TransferenciaID}>
                    <TableRow sx={{ backgroundColor: transferType.color }}>
                      <TableCell>
                        {transferType.type === 'salida' ? <ArrowUpward color="error" /> : 
                         transferType.type === 'entrada' ? <ArrowDownward color="success" /> : null}
                      </TableCell> */}
                      <TableCell>{transferencia.TransferenciaID}</TableCell>
                      {!isMobile && <TableCell>{transferencia.NúmeroTransferencia}</TableCell>}
                      <TableCell>{transferencia.FechaDocumento}</TableCell>
                      <TableCell>{transferencia.AlmacénOrigen}</TableCell>
                      <TableCell>{transferencia.AlmacénDestino}</TableCell>
                      {!isMobile && <TableCell>
                        {transferencia.Comentarios?.length > 30 
                          ? `${transferencia.Comentarios.substring(0, 30)}...` 
                          : transferencia.Comentarios}
                      </TableCell>}
                      <TableCell>
                        <IconButton
                          onClick={() => handleExpandClick(transferencia.TransferenciaID)}
                          size="small"
                        >
                          {open[transferencia.TransferenciaID] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 8}>
                        <Collapse in={open[transferencia.TransferenciaID]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Detalles de Transferencia
                            </Typography>
                            <Table size="small" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Código Artículo</TableCell>
                                  <TableCell>Descripción</TableCell>
                                  <TableCell>Cantidad</TableCell>
                                  {!isMobile && <TableCell>Almacén</TableCell>}
                                  {/* <TableCell>Precio</TableCell> */}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {transferencia.DETALLE?.DETALLE_TRANSFERENCIA?.map((detalle, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{detalle.CodigoArticulo}</TableCell>
                                    <TableCell>
                                      {isMobile && detalle.DescripcionArticulo?.length > 20 
                                        ? `${detalle.DescripcionArticulo.substring(0, 20)}...` 
                                        : detalle.DescripcionArticulo}
                                    </TableCell>
                                    <TableCell>{detalle.Cantidad}</TableCell>
                                    {!isMobile && <TableCell>{detalle.AlmacenLinea}</TableCell>}
                                    {/* <TableCell>{detalle.Precio}</TableCell> */}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

ListTransferencia.propTypes = {
  user: PropTypes.shape({
    usuario: PropTypes.string
  })
};

export default ListTransferencia;